import React from 'react';
import classNames from 'classnames';
import { SelectroController } from '@aha-app/selectro-v2';
import { useController, ApplicationView } from '@aha-app/mvc';
import { AhaTooltip } from 'components/common/reactifyWebComponent';

function useTruncatedObserver() {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    if (ref.current) {
      const isOverflowing =
        ref.current.scrollWidth > ref.current.clientWidth ||
        ref.current.scrollHeight > ref.current.clientHeight;
      setIsTruncated(isOverflowing);
    }
  }, [ref]);

  return { ref, isTruncated };
}

// Projects Menu V2 Trigger Template
interface ProjectsMenuV2TriggerTemplateProps {
  noneSelected?: string;
  items: any[];
  className?: string;
}

const ProjectsMenuV2TriggerTemplate: React.FC<
  ProjectsMenuV2TriggerTemplateProps
> = ({ items, className = 'selectro-trigger', noneSelected }) => {
  const { ref, isTruncated } = useTruncatedObserver();
  const { state } = useController(SelectroController);
  const isOpen = state?.open;
  const item = items[0];

  const displayText = items.length
    ? item.text
    : typeof noneSelected !== 'undefined'
    ? noneSelected
    : state?.text.noneSelected;

  const isTeam = item?.data?.isTeam;
  const isProductLine = item?.data?.isProductLine;

  return (
    <>
      <button type='button' className={className}>
        {items.length !== 0 && isTeam && (
          <i className='fa-regular fa-users tw-text-xs' aria-hidden='true' />
        )}
        <span
          ref={ref}
          className={classNames('selectro-trigger-content', {
            'selectro-trigger-content--product-line ': isProductLine,
          })}
        >
          {displayText}
        </span>
      </button>
      {!isOpen && isTruncated && (
        <AhaTooltip placement='bottom' showDelay={200}>
          {displayText}
        </AhaTooltip>
      )}
    </>
  );
};

// Projects Menu V2 Item Template
interface ProjectsMenuV2ItemTemplateProps {
  item: any;
  tabIndex: number;
}

const ProjectsMenuV2ItemTemplate: React.FC<ProjectsMenuV2ItemTemplateProps> = ({
  item,
  tabIndex,
}) => {
  const { ref, isTruncated } = useTruncatedObserver();
  const value = item.value;
  const text = item.text;
  const isTeam = item.data.isTeam;
  const hasParent = item.parent && !item.parent.data?.isHeading;
  const parentIsTeam = item.parent?.data?.isTeam;
  const isVirtualTeam = hasParent && isTeam && !parentIsTeam;
  const isMyWork = !!item.data.myWorkLink;
  const isWorkspace =
    !item.data.isProductLine &&
    !item.data.isVirtualTeam &&
    !item.data.isHeading;
  return (
    <>
      <div
        className='SelectroV2__text'
        tabIndex={tabIndex}
        role='option'
        data-value={value}
      >
        {isTeam && <i className='fa-regular fa-users' aria-hidden='true' />}
        {isMyWork && (
          <i className='fa-regular fa-briefcase' aria-hidden='true' />
        )}
        <span
          ref={ref}
          className={classNames('SelectroV2__text-span', {
            'SelectroV2__text-span--virtual-team': isVirtualTeam,
            'SelectroV2__text-span--product-line': item.data.isProductLine,
            'SelectroV2__text-span--heading': item.data.isHeading,
            'SelectroV2__text-span--workspace': isWorkspace,
          })}
        >
          {text}
        </span>
      </div>
      {isTruncated && (
        <AhaTooltip showDelay={200} class='tw-text-wrap'>
          {text}
        </AhaTooltip>
      )}
    </>
  );
};

// Export the components
export const ProjectsMenuV2Templates = {
  trigger: ApplicationView(ProjectsMenuV2TriggerTemplate),
  item: ProjectsMenuV2ItemTemplate,
};

export default ProjectsMenuV2Templates;
